// /admin/usage — per-org usage/metering totals card + CSV export (T367).
//
// Backend: GET /api/v1/admin/usage(?since_days=N), GET
// /api/v1/admin/usage/export.csv. org_admin role required; available to
// every license tier (billing/metering, not a gated enterprise Feature).

import { ReactNode, useState } from 'react';
import useSWR from 'swr';
import { Activity, Download, FolderGit2, KeyRound, Loader2, ScanLine, Ticket, Users } from 'lucide-react';
import { ApiError, UsageTotals, adminUsageApi, downloadReport } from '../lib/api';
import { PageHeader } from '../components/ui';

const WINDOWS: { label: string; sinceDays?: number }[] = [
  { label: 'All time' },
  { label: 'Last 30 days', sinceDays: 30 },
  { label: 'Last 7 days', sinceDays: 7 },
];

// T621: known feature_counts keys, in display order. Keep in sync with
// db.UsageFeature* (api/internal/db/usage.go).
const FEATURE_LABELS: { key: string; label: string; Icon: typeof Activity }[] = [
  { key: 'sso_login', label: 'SSO logins', Icon: KeyRound },
  { key: 'report_exported', label: 'Reports exported', Icon: FolderGit2 },
  { key: 'deep_taint_scan', label: 'Deep-taint scans', Icon: ScanLine },
  { key: 'jira_issue_created', label: 'Jira issues created', Icon: Ticket },
];

function fmtNumber(n: number): string {
  return n.toLocaleString();
}

function usageKey(sinceDays?: number): string {
  return `/api/v1/admin/usage${sinceDays === undefined ? '' : `?since_days=${sinceDays}`}`;
}

export function AdminUsage() {
  const [sinceDays, setSinceDays] = useState<number | undefined>(undefined);
  const { data, error, isLoading } = useSWR<UsageTotals>(
    usageKey(sinceDays),
    () => adminUsageApi.get({ sinceDays }),
    { revalidateOnFocus: false },
  );
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const filename = `usage-${new Date().toISOString().slice(0, 10)}.csv`;
      await downloadReport(adminUsageApi.exportCSVURL({ sinceDays }), filename);
    } finally {
      setDownloading(false);
    }
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading usage totals…</span>
      </div>
    );
  } else if (error) {
    body = <ErrorBanner error={error} />;
  } else if (data) {
    body = <UsageCards data={data} />;
  } else {
    body = null;
  }

  return (
    <div className="max-w-3xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Activity />}
        title="Usage"
        description={
          <>
            Scan volume, lines analyzed, active projects, and seats — for
            billing and capacity planning.
          </>
        }
        actions={
          <button
            type="button"
            onClick={handleExport}
            disabled={downloading || !data}
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md shrink-0
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-bg-inset text-fg hover:bg-border border border-border
              transition-colors disabled:opacity-30 disabled:cursor-not-allowed
            "
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            Export CSV
          </button>
        }
      />

      <div className="orb-card p-1 mb-6 inline-flex gap-1" role="tablist" aria-label="Reporting window">
        {WINDOWS.map((w) => (
          <button
            key={w.label}
            type="button"
            role="tab"
            aria-selected={sinceDays === w.sinceDays}
            onClick={() => setSinceDays(w.sinceDays)}
            className={`
              px-3 py-1.5 rounded text-xs font-semibold transition-colors
              ${sinceDays === w.sinceDays ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'}
            `}
          >
            {w.label}
          </button>
        ))}
      </div>

      {body}
    </div>
  );
}

function UsageCards({ data }: { readonly data: UsageTotals }) {
  const tiles = [
    { label: 'Scans completed', value: fmtNumber(data.scans_completed), Icon: ScanLine },
    { label: 'Lines scanned', value: fmtNumber(data.loc_scanned), Icon: Activity },
    { label: 'Active projects', value: fmtNumber(data.active_projects), Icon: FolderGit2 },
    { label: 'Seats', value: fmtNumber(data.seats), Icon: Users },
  ];
  const featureCounts = data.feature_counts ?? {};
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {tiles.map((t) => (
          <div key={t.label} className="orb-card p-5 flex items-center gap-4">
            <t.Icon className="h-6 w-6 text-accent shrink-0" aria-hidden="true" />
            <div>
              <p className="text-2xl font-display font-semibold text-fg">{t.value}</p>
              <p className="text-xs text-fg-muted">{t.label}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="orb-eyebrow mt-8 mb-3">Gated-feature usage</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {FEATURE_LABELS.map((f) => (
          <div key={f.key} className="orb-card p-5 flex items-center gap-4">
            <f.Icon className="h-6 w-6 text-accent shrink-0" aria-hidden="true" />
            <div>
              <p className="text-2xl font-display font-semibold text-fg">
                {fmtNumber(featureCounts[f.key] ?? 0)}
              </p>
              <p className="text-xs text-fg-muted">{f.label}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can view usage reporting.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
